import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import {
  ContentControlledEditingDirectiveViewModel,
  SegmentExtender,
  SegmentExtenderKeyframe,
} from 'app/js/src/domain/content-control/editing-directive';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';

import { ETimelineMode } from 'app/js/src/domain/content-control/studio/e-timeline-mode';
import { ContentControlStudioLiveViewModelV2 } from '../content-control-studio-live-view-model-v2';
import {
  ContentControlStudioBaseTimelineComponent,
  TimeBlock,
  TimeBlockTick,
  TooltipPosition,
} from './content-control-studio-base-timeline.component';
import { VideoTimeFrameMetadata } from './content-control-studio-video-metadata';
import { LiveTimelineCoordinateSystem } from './live-timeline-coordinate-system';

export { KeyframeClickPayload } from './content-control-studio-base-timeline.component';

/**
 * Live-stream variant of the studio timeline.
 *
 * Uses a unified coordinate system ({@link LiveTimelineCoordinateSystem}) where
 * all internal math operates in **Timeline Seconds (TS)** —
 * `ts = (utcMs - originMs) / 1000`. Conversions to/from player-relative seconds,
 * UTC timestamps, and pixels happen only at the coordinate-system boundary.
 *
 * Key behaviours:
 * - Absolute positioning: segments and playhead positions are computed from source
 *   coordinates every frame, eliminating incremental drift.
 * - `pixelsPerSecond` is derived as `containerWidth / viewportDuration` (non-circular).
 * - ET mode is handled inside `timelineSecondsToPixel`/`pixelToTimelineSeconds` via `otToEt`/`etToOt`.
 * - Fit-segment mode sets the viewport to the segment range with margins.
 * - Auto-scrolling follows the live edge at ~85% from the left.
 *
 * Receives its state through the {@link ContentControlStudioLiveViewModelV2} input.
 */
@Component({
  selector: 'evrideo-content-control-studio-live-timeline-v2',
  templateUrl: './content-control-studio-timeline.component.html',
  styleUrl: './content-control-studio-timeline.component.scss',
})
export class ContentControlStudioLiveTimelineComponentV2
  extends ContentControlStudioBaseTimelineComponent
  implements OnDestroy
{
  // -------------------------------------------------------------------------
  // @Input/@Output (live-specific)
  // -------------------------------------------------------------------------

  /** The live studio view model that drives this timeline's state and playback. */
  @Input('studio-view-model') public override vm!: ContentControlStudioLiveViewModelV2;

  // -------------------------------------------------------------------------
  // Private fields (live-specific)
  // -------------------------------------------------------------------------

  /** The unified coordinate system for all timeline math. */
  private _coords = new LiveTimelineCoordinateSystem();

  /** Number of ruler blocks visible in the container at any zoom level. */
  private static readonly VISIBLE_BLOCKS = 6;

  /**
   * Total content duration in seconds (full buffer + padding).
   * Used by `_rebuildTimeBlocks` to build ruler blocks for the entire
   * scrollable area, independent of the viewport's visible window.
   */
  private _totalContentDurationSecs = 0;

  /** TS of the first fragment — left edge of the content area. */
  private _contentStartTimelineSeconds = 0;

  /**
   * UTC timestamp (ms) of the most recent click-seek, held until the player
   * catches up. Prevents the playhead from snapping back to a stale position
   * while HLS rebuffers after a seek.
   */
  private _pendingSeekTimestampMs: number | null = null;

  /** Stops the animation loop on destroy. */
  private _destroyed = false;

  /** Tracks the ET mode state from the last segment rebuild to detect mode changes. */
  private _lastRebuildEtMode = false;

  /** Returns the current editing directive segments from the view model. */
  protected get _editingDirectives(): Array<ContentControlledEditingDirectiveViewModel> {
    return this.vm?.segments ?? [];
  }

  // -------------------------------------------------------------------------
  // Constructor
  // -------------------------------------------------------------------------

  constructor(renderer: Renderer2, cd: ChangeDetectorRef) {
    super(renderer, cd);
  }

  // -------------------------------------------------------------------------
  // Lifecycle hooks
  // -------------------------------------------------------------------------

  /** Stops the animation loop and delegates to the base class for listener cleanup. */
  public override ngOnDestroy(): void {
    this._destroyed = true;
    super.ngOnDestroy();
  }

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  /**
   * Pauses the live stream before initiating timeline drag.
   * Skips when a mark drag is already active (the native mousedown bubbles
   * from the mark handle up to the timeline div — we must not also start a pan).
   */
  public override onTimelineMouseDown(event: MouseEvent | TouchEvent): void {
    if (this._isCurrentSegmentInMarkDragging || this._isCurrentSegmentOutMarkDragging) return;
    this.vm.pause();
    super.onTimelineMouseDown(event);
  }

  /**
   * Updates the tooltip with the UTC timestamp at the mouse position.
   * Converts pixel offset to UTC via the coordinate system.
   */
  public override onMouseMove(event: MouseEvent): void {
    if (!this._currentEditingVideoTimeFrame) return;

    const mouseX = this._calculateTimelineMouseXPx(event);

    this._updateTooltip(mouseX, this.vm.timelineMode);
    this._tooltipPosition = new TooltipPosition(event.clientX, event.clientY - 20);
    this._showTooltip = true;
  }

  /**
   * Initializes the live timeline once the video source is ready.
   * Initializes the coordinate system from the stable playlist start timestamp,
   * starts the animation loop, and calculates the initial layout.
   */
  public override onVideoMetadataLoaded(): void {
    this._coords.initialize(
      this.vm.hlsVideoSource.playlistStartTimestampMs,
      this.vm.hlsVideoSource,
      this.oneFrameSecs,
    );
    this._coords.updateCutSegments(this.vm.segments);
    this._coords.setEtMode(this._isET());

    this._startAnimationLoop();
    this.setPlayheadHeight();
    this._calculateVideoTimeline();
  }

  // -------------------------------------------------------------------------
  // Public methods (overrides)
  // -------------------------------------------------------------------------

  /**
   * Returns the visible timeline duration in seconds.
   * In fit-segment mode, returns the active segment span plus margins.
   * In ET mode, subtracts the total duration of cut segments.
   * Otherwise, returns the full live stream buffer duration.
   */
  public override currentVideoTimeSecs(isFitSegment: boolean, isEditedTimeline: boolean): number {
    if (!this.vm) return 0;

    if (isFitSegment) {
      return (
        this._currentEditingVideoTimeFrame.endsAtSecs -
        this._currentEditingVideoTimeFrame.startsAtSecs +
        this._currentEditingVideoTimeFrame.marginStartSecs +
        this._currentEditingVideoTimeFrame.marginEndSecs
      );
    }

    if (isEditedTimeline) {
      const totalCutSecs = this._coords.totalCutDurationSecs;
      if (totalCutSecs > 0) {
        return (
          this.vm.hlsVideoSource.maxBufferLengthSecs -
          (totalCutSecs + this._coords.cutSegmentCount * this.oneFrameSecs)
        );
      }
    }

    return this.vm.hlsVideoSource.maxBufferLengthSecs;
  }

  /** Returns the current playback position in seconds from the view model. */
  public override currentVideoPlayTimeSecs(): number {
    if (!this.vm) return 0;
    return this.vm.hlsVideoSource.activeFrag ? this.vm.hlsVideoSource.rawCurrentTimeSecs : 0;
  }

  /**
   * Returns the playlist end time as a UTC timestamp in milliseconds for UI display.
   * Unlike VOD (which returns video-relative ms), live uses the absolute playlist end time.
   */
  public override currentVideoPlayTimeForUI(): number {
    return this.vm?.hlsVideoSource.playlistEndTimeAsTimestampMs ?? 0;
  }

  /** Returns the current pixel density from the coordinate system. */
  public override pixelsPerSecond(): number {
    return this._coords.pixelsPerSecond;
  }

  /**
   * Seeks the video and records the target UTC timestamp so the playhead
   * holds position until the player catches up (prevents snap-back during
   * HLS rebuffering after frame-steps, cut-skips, etc.).
   */
  public override seekToVideoPlayerRelativeTimeInSecs(timeSecs: number): void {
    const utcMs = this.vm.hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(timeSecs);
    if (utcMs > 0) {
      this._pendingSeekTimestampMs = utcMs;
    }
    this._videoSeekToTime(timeSecs);
  }

  /**
   * Delegates seeking to the live view model.
   * @param seekTimeSecs - Target time in video-relative seconds.
   */
  protected override _videoSeekToTime(seekTimeSecs: number): void {
    this.vm.hlsVideoSource.seekToVideoPlayerRelativeTimeInSecs(seekTimeSecs);
  }

  /**
   * Finds a cut segment overlapping the given time in seconds.
   * Converts player-relative seconds to a UTC timestamp and searches the segments list.
   * @param timeToCheckSecs - Player-relative time in seconds.
   * @returns The matching cut segment, or `undefined` if none found.
   */
  public override getCutSegmentAtSpecificTime(
    timeToCheckSecs: number,
  ): ContentControlledEditingDirectiveViewModel | undefined {
    let timeToCheckUtcMs = this.vm
      ? this.vm.hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(timeToCheckSecs)
      : timeToCheckSecs * 1000;
    const conversionReturned0 = timeToCheckUtcMs === 0 && timeToCheckSecs > 0;
    if (conversionReturned0 && this.vm) {
      timeToCheckUtcMs = this.vm.hlsVideoSource.currentVideoTimeUtcMs;
    }

    const result = this.vm.segments.find(
      (x) =>
        x.action === ContentControlledEventActionEnum.Cut &&
        x.startsAtMs <= timeToCheckUtcMs &&
        x.endsAtMs >= timeToCheckUtcMs,
    );

    return result;
  }

  /**
   * Calculates the seek time (in seconds) to jump past a cut segment's end.
   * Converts the UTC timestamp to player-relative seconds via the coordinate system,
   * snaps to the nearest frame boundary, and advances by one frame.
   * @param cutEndMs - The cut segment's end time as a UTC timestamp in milliseconds.
   */
  public override getSeekTimeAfterCutEnd(cutEndMs: number): number {
    const cutEndTimelineSeconds = this._coords.utcMsToTimelineSeconds(cutEndMs);
    const playerSecs = this._coords.timelineSecondsToPlayerSecs(cutEndTimelineSeconds);
    const snappedTime = this.vm.getVideoSource().currentPlayTimeAsFrameIndex(playerSecs) / this.videoFps;
    let result = snappedTime + this.oneFrameSecs;
    const currentPlayerTime = this.vm.hlsVideoSource.activeFrag ? this.vm.hlsVideoSource.rawCurrentTimeSecs : 0;
    if (result <= currentPlayerTime) {
      result = currentPlayerTime + this.oneFrameSecs;
    }
    return result;
  }

  /**
   * Seeks the video to the in or out point of the active segment.
   * Uses the coordinate system for UTC -> player-relative conversion.
   * Does not pause the player to avoid falling behind the live edge.
   */
  public override goToSelectedEventTime(point: 'in' | 'out'): void {
    let seekTimeSecs = 0;
    const active = this.getActiveSegment();
    const videoSource = this.vm.getVideoSource();
    if (active) {
      const segTimelineSeconds = this._coords.utcMsToTimelineSeconds(
        point === 'in' ? active.item.startsAtMs : active.item.endsAtMs,
      );
      seekTimeSecs = videoSource.currentPlayTimeAsClosestFrameSecs(
        this._coords.timelineSecondsToPlayerSecs(segTimelineSeconds),
      );
    } else if (point === 'out') {
      seekTimeSecs = this.vm.hlsVideoSource.lastFragment?.start ?? 0;
    }
    this._videoSeekToTime(seekTimeSecs);
  }

  /**
   * Scrolls the timeline viewport to keep the playhead visible.
   * In non-fitted mode, derives scroll position from the coordinate system.
   * @param type - `'afterZoom'` centers around the playhead, `'next'` keeps current position,
   *   `'previous'` scrolls back by one viewport width.
   */
  public override translateTimeline(type: 'afterZoom' | 'next' | 'previous' = 'afterZoom'): void {
    let scrollLeftPx = 0;
    if (this._isFitted() === false) {
      const currentTimelineSeconds = this._coords.playerSecsToTimelineSeconds(this.currentVideoPlayTimeSecs());
      const currentPx = this._coords.timelineSecondsToPixel(currentTimelineSeconds);
      const clientWidth = this.Timeline.nativeElement.clientWidth;
      const totalPx = this._timelineBlocksLengthPx;

      switch (type) {
        case 'afterZoom': {
          if (currentPx > clientWidth / 2) {
            scrollLeftPx = currentPx - clientWidth / 2;
            const pixelsLeft = totalPx - currentPx;
            if (pixelsLeft < clientWidth / 2) {
              scrollLeftPx = Math.max(0, totalPx - clientWidth);
            }
          }
          break;
        }
        case 'next': {
          scrollLeftPx = currentPx;
          break;
        }
        case 'previous': {
          scrollLeftPx = Math.max(0, currentPx - clientWidth);
          break;
        }
      }
      scrollLeftPx = Math.max(0, scrollLeftPx);
    }

    this._renderer.setProperty(
      this.TimelineVirtualScroll.elementRef.nativeElement,
      'scrollLeft',
      scrollLeftPx,
    );
    this._translateTracks(scrollLeftPx);
  }

  /**
   * Rebuilds the visual segment extenders from the current editing directives.
   * Uses the coordinate system for absolute pixel positioning.
   * @param segments - Optional subset of segments to rebuild; defaults to all view model segments.
   */
  public override rebuildSegmentsInternally(
    segments?: Array<ContentControlledEditingDirectiveViewModel>,
  ): void {
    const currentEtMode = this._isET();
    const etModeChanged = currentEtMode !== this._lastRebuildEtMode;
    this._lastRebuildEtMode = currentEtMode;

    this._coords.setEtMode(currentEtMode);
    this._coords.updateCutSegments(this.vm.segments);

    let tempSegments = segments ? [...segments] : [...this.vm.segments];
    tempSegments = this._filterSegmentsByFitMode(tempSegments);
    if (!etModeChanged) {
      tempSegments = this._filterSegmentsNeedingUpdate(tempSegments);
    }

    let updatedSegments = tempSegments
      .map((segment) => this._buildLiveSegmentExtender(segment))
      .toSorted((a, b) => a.item.startsAtMs - b.item.startsAtMs);

    const updatedSegmentIds = updatedSegments.map((x) => x.item.id);
    updatedSegments = [
      ...this._internalSegments.filter(
        (x) => !updatedSegmentIds.includes(x.item.id),
      ),
      ...updatedSegments,
    ];

    this._internalSegments = [...updatedSegments];
    this._updateSegmentArrays(updatedSegments);
  }

  // -------------------------------------------------------------------------
  // Protected method overrides
  // -------------------------------------------------------------------------

  /**
   * Recalculates the entire live timeline layout.
   *
   * The viewport determines the **visible window** (and thus pps / scale):
   * - Fitted mode: viewport = full content → everything fits, no scrolling.
   * - Zoomed mode: viewport = `blockDuration × VISIBLE_BLOCKS` → content wider
   *   than the container → scrolling required.
   *
   * The ruler blocks always span the full content duration, so the scrollable
   * area extends as far as the buffer allows.
   */
  protected override _calculateVideoTimeline(): void {
    if (!this.vm || this.currentVideoTimeSecs(this.isFitSegment, this._isET()) === 0) return;

    this._coords.setEtMode(this._isET());
    this._coords.updateCutSegments(this.vm.segments);

    const timeframe = this._buildCurrentTimelineFrame();
    if (!timeframe) return;
    this._currentEditingVideoTimeFrame = timeframe;

    const containerWidthPx = this._getContainerWidthPx();

    if (this.isFitSegment) {
      this._coords.setFitSegmentViewport(
        this._coords.utcMsToTimelineSeconds(timeframe.startsAtSecs * 1000),
        this._coords.utcMsToTimelineSeconds(timeframe.endsAtSecs * 1000),
        this._fitSegmentMarginDivisor,
        containerWidthPx,
        this._coords.utcMsToTimelineSeconds(this.vm.hlsVideoSource.playlistEndTimeAsTimestampMs),
      );
      this._totalContentDurationSecs = this._coords.viewportDurationSecs;
      this._contentStartTimelineSeconds = this._coords.viewportStartTimelineSeconds;
    } else {
      // Anchor to the current first fragment — tracks the HLS sliding window.
      const firstFragPdt = this.vm.hlsVideoSource.firstFragment?.programDateTime
        ?? this._coords.originMs;
      const startTimelineSeconds = this._coords.utcMsToTimelineSeconds(firstFragPdt);
      const totalDurationSecs = this.currentVideoTimeSecs(false, this._isET())
        + this.vm.hlsVideoSource.segmentDurationSecs;

      this._contentStartTimelineSeconds = startTimelineSeconds;
      this._totalContentDurationSecs = totalDurationSecs;

      if (this._isFitted()) {
        // Max zoom out: viewport = full content → everything fits
        this._coords.setViewport(startTimelineSeconds, totalDurationSecs, containerWidthPx);
      } else {
        // Zoomed in: viewport = visible window based on zoom level.
        // pps increases → content becomes wider than container → scrolling.
        const visibleDurationSecs = Math.min(
          this.timelineBlockDurationSecs * ContentControlStudioLiveTimelineComponentV2.VISIBLE_BLOCKS,
          totalDurationSecs,
        );
        this._coords.setViewport(startTimelineSeconds, visibleDurationSecs, containerWidthPx);
      }
    }

    // Always recalculate block width — zoom level may have changed
    this._calculateTimelineBlockWidth();

    this._rebuildTimeBlocks(this.vm.timelineMode);
    this._scheduleUIRefresh();
  }

  /**
   * Builds video player metadata for the current timeline view.
   * In fit-segment mode, scopes to the active segment with margins.
   * Otherwise, returns the full playlist time range.
   */
  protected _buildCurrentTimelineFrame(): VideoTimeFrameMetadata | null {
    if (this.isFitSegment) {
      const segment = this.getActiveSegment();
      if (!segment) return null;
      const timeframe = new VideoTimeFrameMetadata(
        segment.item.startsAtMs / 1000,
        segment.item.endsAtMs / 1000,
        0,
        0,
      );
      const marginSecs = (timeframe.endsAtSecs - timeframe.startsAtSecs) / this._fitSegmentMarginDivisor;
      timeframe.marginStartSecs += marginSecs <= timeframe.startsAtSecs ? marginSecs : timeframe.startsAtSecs;
      // Use playlist end time instead of durationSecs (which is Infinity for live)
      const playlistEndSecs = this.vm.hlsVideoSource.playlistEndTimeAsTimestampSecs;
      const remainingToEndSecs = playlistEndSecs - timeframe.endsAtSecs;
      timeframe.marginEndSecs += marginSecs <= remainingToEndSecs ? marginSecs : Math.max(remainingToEndSecs, 0);
      return timeframe;
    }
    return new VideoTimeFrameMetadata(
      this.vm.hlsVideoSource.currentTimeSecs,
      this.vm.hlsVideoSource.playlistEndTimeAsTimestampSecs,
      0,
      0,
    );
  }

  /**
   * Builds the timeline ruler blocks with UTC-based timestamps.
   *
   * Always spans the full content duration ({@link _totalContentDurationSecs}),
   * not just the viewport's visible window. When zoomed in, this creates a
   * scrollable ruler wider than the container.
   */
  protected _rebuildTimeBlocks(timelineType: ETimelineMode): void {
    const tickWidthPx = 1;
    const timeBlocks: TimeBlock[] = [];
    let remainingDurationSecs = this._totalContentDurationSecs;
    let blockOffsetSecs = 0;
    const blockDurationSecs = this.timelineBlockDurationSecs;

    while (remainingDurationSecs > 0) {
      const ticks: TimeBlockTick[] = [];
      const isLastBlock = remainingDurationSecs < blockDurationSecs;

      ticks.push(new TimeBlockTick(0, 35, 45, tickWidthPx));

      const actualBlockDurationSecs = isLastBlock ? remainingDurationSecs : blockDurationSecs;
      for (
        let i = 1;
        i < TimeBlock.TICKS_NUMBER && (i * actualBlockDurationSecs) / TimeBlock.TICKS_NUMBER < remainingDurationSecs;
        i++
      ) {
        ticks.push(
          new TimeBlockTick(i * (this._timelineBlockWidthPx / TimeBlock.TICKS_NUMBER), 40, 45, tickWidthPx),
        );
      }

      const blockWidthPx = isLastBlock
        ? this._timelineBlockWidthPx * (remainingDurationSecs / blockDurationSecs)
        : this._timelineBlockWidthPx;

      // Block timestamp in UTC ms — derived from the content start TS
      const blockTimelineSeconds = this._contentStartTimelineSeconds + blockOffsetSecs;
      let timestampMs = this._coords.timelineSecondsToUtcMs(blockTimelineSeconds);
      
      if (timelineType === ETimelineMode.EditedTime) {
        timestampMs += this._coords.viewportDurationSecs * 1000;
      }
      timeBlocks.push(new TimeBlock(ticks, blockWidthPx, timestampMs, -23));

      blockOffsetSecs += blockDurationSecs;
      remainingDurationSecs -= blockDurationSecs;
    }

    this._timelineBlocksLengthPx = timeBlocks
      .map((x) => x.widthPx)
      .reduce((partial, a) => partial + a, 0);

    this._timeBlocks = timeBlocks;
  }

  /**
   * Schedules a deferred UI refresh after timeline recalculation.
   * Triggers change detection via `markForCheck`.
   */
  protected _scheduleUIRefresh(): void {
    setTimeout(() => {
      this.translateTimeline();
      this.TimelineVirtualScroll.checkViewportSize();
      this._resizeTracks();
      this.rebuildSegmentsInternally();
      this._cd.markForCheck();
    }, 0);
  }

  /**
   * Handles a click on the timeline ruler to seek the playhead.
   * Only seeks when called with an explicit user event (not from
   * `onChangeEffectingTimeline`) and no mark drag is active.
   */
  protected override _onTimelineClickedEventHandler(event?: MouseEvent): void {
    if (!event) return; // Called from onChangeEffectingTimeline — animation loop handles playhead

    if (
      this._isPlayheadDragging === false &&
      this._isTimelineDragging === false &&
      this._isCurrentSegmentInMarkDragging === false &&
      this._isCurrentSegmentOutMarkDragging === false &&
      this._tooltipCurrentTime
    ) {
      
      const mouseXpx = this._calculateTimelineMouseXPx(event);
      
      const mouseTimestampMs = this._coords.pixelToUtcMs(mouseXpx);
      const timelineSeconds = this._coords.utcMsToTimelineSeconds(mouseTimestampMs);
      this._videoSeekToTime(this._coords.timelineSecondsToPlayerSecs(timelineSeconds));
      this._pendingSeekTimestampMs = mouseTimestampMs;
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /** Delegates all live drag interactions to {@link _doLiveDrags}. */
  protected override _onWindowMouseMove(e: MouseEvent): void {
    this._doLiveDrags(e);
  }

  /**
   * Converts pixel offset to a UTC timestamp (seconds) via the coordinate system.
   * Used by base class for mark-in/out drag calculations.
   */
  protected override _pixelsToSecondsConvert(pixels: number): number {
    if (!this.vm || this._coords.pixelsPerSecond <= 0) return 0;
    return this._coords.pixelToUtcMs(pixels) / 1000;
  }

  /**
   * Overrides the base class to skip the VOD-specific ET repositioning.
   * The coordinate system already handles ET inside `timelineSecondsToPixel`, so segments
   * have correct absolute positions. Only the overlap-stacking logic is needed.
   */
  protected override _setSimilarSegmentsStyle(segments: SegmentExtender[]): void {
    const findOverlappingSegments = (
      segment: SegmentExtender,
      segmentExtenders: Array<SegmentExtender>,
    ): SegmentExtender[] => {
      return segmentExtenders.filter((segmentExtender) => {
        const one = segmentExtender.item.startsAtMs <= segment.item.startsAtMs;
        const two = segmentExtender.item.endsAtMs >= segment.item.startsAtMs;
        const three = segment.item.startsAtMs <= segmentExtender.item.startsAtMs;
        const four = segment.item.endsAtMs >= segmentExtender.item.startsAtMs;
        return (one && two) || (three && four);
      });
    };

    segments.forEach((segment) => {
      // No ET repositioning — coordinate system handles it via timelineSecondsToPixel
      const overlaps = findOverlappingSegments(segment, segments);
      segment.style = {};
      if (overlaps && overlaps.length > 0) {
        overlaps.sort((a, b) => a.item.startsAtMs - b.item.startsAtMs);
        const index = overlaps.findIndex((x) => x.item.id === segment.item.id);
        const length = overlaps.length;
        const active = this.getActiveSegment();
        segment.style = {
          height: `${100 / length}%`,
          top: `${(100 / length) * index}%`,
          opacity:
            this.isFitSegment && active && segment.item.id !== active.item.id
              ? '0.4'
              : '1',
        };
      }
    });
  }

  // -------------------------------------------------------------------------
  // Private computed getters (live-specific)
  // -------------------------------------------------------------------------

  /** Whether the underlying video element is currently paused. */
  private get _videoIsPaused(): boolean {
    return this.vm.getVideoSource().paused;
  }

  /** Current horizontal scroll offset (px) of the virtual-scroll viewport. */
  private get _liveVirtualScrollLeftPx(): number {
    return this.TimelineVirtualScroll.measureScrollOffset();
  }

  /**
   * Effective pixel width of the live timeline.
   * In fitted mode returns the visible container width; otherwise the total ruler width.
   */
  private get _liveTimelineWidth(): number {
    return this._isFitted()
      ? this.Timeline.nativeElement.offsetWidth
      : this._timelineBlocksLengthPx;
  }

  // -------------------------------------------------------------------------
  // Private methods — animation loop
  // -------------------------------------------------------------------------

  /** Kicks off the `requestAnimationFrame`-based animation loop. */
  private _startAnimationLoop(): void {
    this._lastFrameTime = performance.now();
    requestAnimationFrame((t: number) => this._updateUILoop(t));
  }

  /**
   * Core animation callback.
   *
   * Each frame: computes the current TS position, optionally follows the live edge,
   * updates the playhead, and schedules the next frame.
   */
  private _updateUILoop(currentTime: number): void {
    if (this._destroyed) return;

    const elapsed = currentTime - this._lastFrameTime;
    // Throttle to ~60fps
    if (elapsed < 14) {
      requestAnimationFrame((t: number) => this._updateUILoop(t));
      return;
    }

    if (this._isPlayheadDragging === false) {
      this._updatePlayheadPosition();
      this._enforceFitSegmentBoundary();
    }

    if (this._showTooltip && !this._videoIsPaused) {
      this._tooltipCurrentTime += elapsed;
      this._tooltipFormattedCurrentTime = this.vm.timeFormatFunction(
        this._tooltipCurrentTime,
      );
    }

    this._lastFrameTime = currentTime;
    requestAnimationFrame((t: number) => this._updateUILoop(t));
  }

  /**
   * Positions the playhead element based on the current UTC playback time.
   *
   * Uses the coordinate system for a single, correct conversion:
   * `playerSecs -> TS -> pixel`. The viewport is NEVER modified here — it only
   * changes on rebuild ({@link _calculateVideoTimeline}). This ensures the
   * playhead, ruler, and segments always share the same coordinate frame.
   */
  protected _updatePlayheadPosition(): void {
    if (!this.vm || !this.vm.hlsVideoSource.activeFrag) return;

    const playerSecs = this.vm.hlsVideoSource.rawCurrentTimeSecs;
    let currentTimelineSeconds = this._coords.playerSecsToTimelineSeconds(playerSecs);

    // After a click-seek, the player may lag behind for a few frames
    if (this._pendingSeekTimestampMs !== null) {
      const pendingTimelineSeconds = this._coords.utcMsToTimelineSeconds(this._pendingSeekTimestampMs);
      const diffSecs = Math.abs(currentTimelineSeconds - pendingTimelineSeconds);
      if (diffSecs < 1) {
        this._pendingSeekTimestampMs = null;
      } else {
        currentTimelineSeconds = pendingTimelineSeconds;
      }
    }

    const playheadPx = this._coords.timelineSecondsToPixel(currentTimelineSeconds);
    if (playheadPx < 0) return;

    // In non-fitted mode, the entire buffer fits in the container.
    // Use CSS left directly — no scroll offset needed because the
    // viewport matches the visible content.
    if (this._isFitted()) {
      const timelineLeft = playheadPx - this._playheadHalfWidth;
      this._renderer.setStyle(
        this.Playhead.nativeElement,
        'left',
        `${timelineLeft}px`,
      );
      this._playheadCurrentLeft = this._playheadPreviousLeft = timelineLeft;
      return;
    }

    // Non-fitted: playhead is positioned in content space, adjusted for scroll
    const scrollLeft = this._liveVirtualScrollLeftPx;
    let timelineLeft = playheadPx - scrollLeft - this._playheadHalfWidth;

    // Auto-scroll when playhead goes off-screen left while paused
    if (this._videoIsPaused && timelineLeft < 0) {
      const newScroll = Math.max(0, playheadPx - this._playheadHalfWidth);
      this._renderer.setProperty(
        this.TimelineVirtualScroll.elementRef.nativeElement,
        'scrollLeft',
        newScroll,
      );
      this._translateTracks(newScroll);
      timelineLeft = playheadPx - newScroll - this._playheadHalfWidth;
    }

    this._renderer.setStyle(
      this.Playhead.nativeElement,
      'left',
      `${timelineLeft}px`,
    );
    this._playheadCurrentLeft = this._playheadPreviousLeft = timelineLeft;
  }

  /**
   * Pauses playback when the current playback time exceeds the fit-segment
   * boundary (segment end + margin). Only active when {@link isFitSegment} is true.
   */
  private _enforceFitSegmentBoundary(): void {
    if (!this.isFitSegment || !this.vm?.hlsVideoSource?.playlistStartTimestampMs) return;

    const currentTimelineSeconds = this._coords.playerSecsToTimelineSeconds(this.vm.hlsVideoSource.rawCurrentTimeSecs);
    const endTimelineSeconds = this._coords.utcMsToTimelineSeconds(this._currentEditingVideoTimeFrame.endsAtSecs * 1000);
    const marginEndTimelineSeconds = this._currentEditingVideoTimeFrame.marginEndSecs;

    if (currentTimelineSeconds >= endTimelineSeconds + marginEndTimelineSeconds) {
      this.vm.pause();
    }
  }

  // -------------------------------------------------------------------------
  // Private methods — segment building
  // -------------------------------------------------------------------------

  /**
   * Creates a {@link SegmentExtender} for a single live editing directive.
   * Uses the coordinate system for absolute pixel positioning.
   * @param segment - The editing directive to visualise.
   * @returns A fully positioned segment extender ready for rendering.
   */
  private _buildLiveSegmentExtender(
    segment: ContentControlledEditingDirectiveViewModel,
  ): SegmentExtender {
    const startTimelineSeconds = this._coords.utcMsToTimelineSeconds(segment.startsAtMs);
    const endTimelineSeconds = this._coords.utcMsToTimelineSeconds(segment.endsAtMs);
    const segmentDurationMs = segment.endsAtMs - segment.startsAtMs;

    const leftPx = this._coords.timelineSecondsToPixel(startTimelineSeconds);
    const rightPx = this._coords.timelineSecondsToPixel(endTimelineSeconds);
    const widthPx = rightPx - leftPx;

    const keyframes = this._buildSegmentKeyframes(segment, segmentDurationMs, widthPx);

    return new SegmentExtender(
      segment.id,
      { ...segment } as ContentControlledEditingDirectiveViewModel,
      widthPx,
      100,
      keyframes,
      leftPx,
      {},
    );
  }

  /**
   * Builds the visual keyframe slices for a segment extender.
   * @param segment - The editing directive containing keyframe data.
   * @param segmentDurationMs - Total segment duration in milliseconds.
   * @param segmentWidthPx - Total segment width in pixels.
   * @returns An array of keyframe slices for the segment's visual representation.
   */
  private _buildSegmentKeyframes(
    segment: ContentControlledEditingDirectiveViewModel,
    segmentDurationMs: number,
    segmentWidthPx: number,
  ): SegmentExtenderKeyframe[] {
    const keyframes: SegmentExtenderKeyframe[] = [];

    if (!segment.keyframes || segment.keyframes.length === 0) {
      keyframes.push(new SegmentExtenderKeyframe(segmentWidthPx, false));
      return keyframes;
    }

    const pps = this._coords.pixelsPerSecond;
    let lastKeyframeTime = segment.startsAtMs;
    segment.keyframes
      .toSorted((a, b) => a.timeInMs - b.timeInMs)
      .forEach((k) => {
        const beforeKeyframe =
          ((k.timeInMs - lastKeyframeTime) / 1000) * pps;
        if (beforeKeyframe) {
          keyframes.push(new SegmentExtenderKeyframe(Math.abs(beforeKeyframe), false));
        }
        keyframes.push(
          new SegmentExtenderKeyframe(
            pps / this.videoFps,
            true,
            k.timeInMs,
          ),
        );
        lastKeyframeTime = k.timeInMs + this.oneFrameSecs * 1000;
      });

    if (segment.endsAtMs - lastKeyframeTime > 0) {
      keyframes.push(
        new SegmentExtenderKeyframe(
          Math.abs(((segment.endsAtMs - lastKeyframeTime) / 1000) * pps),
          false,
        ),
      );
    }

    return keyframes;
  }

  /**
   * In fit-segment mode, keeps only segments that overlap the active segment's time range.
   * Returns all segments unchanged when not in fit-segment mode.
   */
  private _filterSegmentsByFitMode(
    segments: Array<ContentControlledEditingDirectiveViewModel>,
  ): Array<ContentControlledEditingDirectiveViewModel> {
    if (!this.isFitSegment) return segments;
    const active = this.getActiveSegment();
    if (!active) return segments;
    return segments.filter((x) => {
      const startsBeforeActive = x.startsAtMs <= active.item.startsAtMs;
      const endsAfterActiveStart = x.endsAtMs >= active.item.startsAtMs;
      const activeStartsBefore = active.item.startsAtMs <= x.startsAtMs;
      const activeEndsAfterStart = active.item.endsAtMs >= x.startsAtMs;
      return (startsBeforeActive && endsAfterActiveStart) || (activeStartsBefore && activeEndsAfterStart);
    });
  }

  /**
   * Filters segments to only those that are new, have changed properties,
   * or are still within the visible playlist range.
   */
  private _filterSegmentsNeedingUpdate(
    segments: Array<ContentControlledEditingDirectiveViewModel>,
  ): Array<ContentControlledEditingDirectiveViewModel> {
    return segments.filter((seg) => {
      const existingSegment = this._internalSegments.find((x) => x.item.id === seg.id);
      if (!existingSegment) return true;
      const hasChanges =
        existingSegment.item.createdAt !== seg.createdAt ||
        existingSegment.item.startsAtMs !== seg.startsAtMs ||
        existingSegment.item.endsAtMs !== seg.endsAtMs ||
        existingSegment.item.action !== seg.action ||
        existingSegment.item.keyframes !== seg.keyframes;
      const isVisible = seg.endsAtMs >= (this.vm.hlsVideoSource.firstFragment?.programDateTime ?? 0);
      return hasChanges && isVisible;
    });
  }

  /**
   * Distributes rebuilt segment extenders into per-media-type arrays
   * and applies visual styling.
   */
  private _updateSegmentArrays(segments: Array<SegmentExtender>): void {
    this._updateInclusiveSegments(segments);
    this._setSimilarSegmentsStyle(this._inclusiveSegments);

    this._audioSegments = this._filterSegments(
      ContentControlledMediaTypeEnum.Audio,
      segments,
    );
    this._setSimilarSegmentsStyle(this._audioSegments);

    this._eventNotificationSegments = this._filterSegments(
      ContentControlledMediaTypeEnum.External,
      segments,
    );
    this._setSimilarSegmentsStyle(this._eventNotificationSegments);

    this._videoSegments = this._filterSegments(
      ContentControlledMediaTypeEnum.Video,
      segments,
    );
    this._setSimilarSegmentsStyle(this._videoSegments);

    this._subtitlesSegments = this._filterSegments(
      ContentControlledMediaTypeEnum.Subtitles,
      segments,
    );
    this._setSimilarSegmentsStyle(this._subtitlesSegments);

    this._graphicsSegments = this._filterSegments(
      ContentControlledMediaTypeEnum.Graphics,
      segments,
    );
    this._setSimilarSegmentsStyle(this._graphicsSegments);
  }

  // -------------------------------------------------------------------------
  // Private methods — drag handling
  // -------------------------------------------------------------------------

  /**
   * Unified handler for all live-mode mouse-drag interactions.
   * Uses the coordinate system for pixel <-> time conversions.
   */
  private _doLiveDrags(e: MouseEvent): void {
    const currentPosition = e.clientX;

    if (this._isTimelineDragging) {
      const dx = currentPosition - this._timelineStartPosPx;
      const scrollEl = this.TimelineVirtualScroll.elementRef.nativeElement;
      const currentScrollLeft = scrollEl.scrollLeft;
      const maxScroll = this._liveTimelineWidth - 2 * this._timelineBlockWidthPx;
      if (currentScrollLeft < maxScroll) {
        this._renderer.setProperty(scrollEl, 'scrollLeft', currentScrollLeft - dx);
        this._translateTracks(this._liveVirtualScrollLeftPx);
      } else {
        this._renderer.setProperty(scrollEl, 'scrollLeft', maxScroll - this._playheadHalfWidth);
      }
      this._timelineStartPosPx = currentPosition;
    }

    if (this._isPlayheadDragging) {
      const dx = currentPosition - this._playheadStartPos;
      this._playheadCurrentLeft =
        this.isVideoTimelineFit === false && this.isFitSegment === false
          ? currentPosition - this.Timeline.nativeElement.getBoundingClientRect().x
          : this._playheadPreviousLeft + dx;
      if (
        this._playheadCurrentLeft <= this._liveTimelineWidth - this._playheadHalfWidth &&
        this._playheadCurrentLeft >= 0
      ) {
        const totalPx = this._playheadCurrentLeft + this._liveVirtualScrollLeftPx;
        const timelineSeconds = this._coords.pixelToTimelineSeconds(totalPx);
        const playerSecs = this._coords.timelineSecondsToPlayerSecs(timelineSeconds);
        const snappedTime = this.vm.getVideoSource().currentPlayTimeAsFrameIndex(playerSecs) / this.videoFps;
        setTimeout(() => {
          this._videoSeekToTime(snappedTime);
        }, 0);
      }
    }

    if (this._isCurrentSegmentInMarkDragging && this.selectedSegmentId) {
      const selectedSegment = this.getActiveSegment();
      if (!selectedSegment) return;

      const dx = currentPosition - this._currentSegmentInMarkStartPos;
      const newLeft = selectedSegment.left + dx;
      const newStartUtcMs = this._coords.pixelToUtcMs(newLeft);
      if (newStartUtcMs < 0 || newStartUtcMs >= selectedSegment.item.endsAtMs) return;

      selectedSegment.item.startsAtMs = newStartUtcMs;
      const newStartTimelineSeconds = this._coords.utcMsToTimelineSeconds(newStartUtcMs);
      const endTimelineSeconds = this._coords.utcMsToTimelineSeconds(selectedSegment.item.endsAtMs);
      selectedSegment.width = this._coords.timelineSecondsToPixel(endTimelineSeconds) - this._coords.timelineSecondsToPixel(newStartTimelineSeconds);
      selectedSegment.left = newLeft;
      this._currentSegmentInMarkStartPos = currentPosition;
      this._internalSegments = [...this._internalSegments];
    }

    if (this._isCurrentSegmentOutMarkDragging && this.selectedSegmentId) {
      const selectedSegment = this.getActiveSegment();
      if (!selectedSegment) return;

      const dx = currentPosition - this._currentSegmentOutMarkStartPos;
      const currentRight = selectedSegment.left + selectedSegment.width;
      const newRight = currentRight + dx;
      const newEndUtcMs = this._coords.pixelToUtcMs(newRight);
      if (
        newEndUtcMs > this.vm.hlsVideoSource.playlistEndTimeAsTimestampMs ||
        newEndUtcMs <= selectedSegment.item.startsAtMs
      ) {
        return;
      }
      selectedSegment.item.endsAtMs = newEndUtcMs;
      const startTimelineSeconds = this._coords.utcMsToTimelineSeconds(selectedSegment.item.startsAtMs);
      const newEndTimelineSeconds = this._coords.utcMsToTimelineSeconds(newEndUtcMs);
      selectedSegment.width = this._coords.timelineSecondsToPixel(newEndTimelineSeconds) - this._coords.timelineSecondsToPixel(startTimelineSeconds);
      this._currentSegmentOutMarkStartPos = currentPosition;
      this._internalSegments = [...this._internalSegments];
    }
  }

  // -------------------------------------------------------------------------
  // Private methods — utilities
  // -------------------------------------------------------------------------

  /**
   * Calculates the mouse X position in pixels relative to the timeline,
   * accounting for the container's left offset and scroll position.
   */
  private _calculateTimelineMouseXPx(event: MouseEvent): number {
    const rectLeft =
      this.TimelineVirtualScroll.measureBoundingClientRectWithScrollOffset('left');
    const scrollOffset = this.TimelineVirtualScroll.measureScrollOffset();
    return Math.max(0, event.clientX - rectLeft + scrollOffset);
  }

  /**
   * Converts a pixel offset to a UTC timestamp and updates the tooltip fields.
   * In Edited Time mode, shifts the timestamp forward by the viewport duration.
   */
  private _updateTooltip(xPx: number, timelineType: ETimelineMode): void {
    this._tooltipCurrentTime = this._coords.pixelToUtcMs(xPx);

    if (timelineType === ETimelineMode.EditedTime) {
      this._tooltipCurrentTime += this._coords.viewportDurationSecs * 1000;
    }

    this._tooltipFormattedCurrentTime = this.vm.timeFormatFunction(
      Math.round(this._tooltipCurrentTime),
    );
  }

  /** Returns the container width in pixels, using the timeline element or a fallback. */
  private _getContainerWidthPx(): number {
    return this.Timeline?.nativeElement?.offsetWidth ?? 800;
  }
}
